import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, PageHeader } from "./ui";
import { breadcrumb } from "./seo";

/* ─── Inline icons ────────────────────────────────────────────────────────── */
function Icon({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-warm-border bg-warm-sand/40 text-warm-accent">
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        {children}
      </svg>
    </span>
  );
}

const ICONS: Record<string, ReactNode> = {
  sales: <path d="M3 5h13v9H3zM16 8h3l2 3v3h-5M5 17a2 2 0 104 0M16 17a2 2 0 104 0" strokeLinecap="round" strokeLinejoin="round" />,
  support: <path d="M21 12a9 9 0 10-3.5 7.1L21 21l-1.1-3.4A8.96 8.96 0 0021 12z" strokeLinecap="round" strokeLinejoin="round" />,
  security: <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3zM9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />,
  careers: <path d="M4 7h16v12H4zM9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M4 12h16" strokeLinecap="round" strokeLinejoin="round" />,
};

type Channel = {
  icon: ReactNode;
  title: string;
  desc: string;
  linkLabel: string;
  href?: string;
  to?: string;
};

const CHANNELS: Channel[] = [
  {
    icon: ICONS.sales,
    title: "Sales & demos",
    desc: "See it on your roles.",
    linkLabel: "hello@wonderfulagent.ai",
    href: "mailto:hello@wonderfulagent.ai",
  },
  {
    icon: ICONS.support,
    title: "Support",
    desc: "Already a customer? We're here — or use in-app help.",
    linkLabel: "support@wonderfulagent.ai",
    href: "mailto:support@wonderfulagent.ai",
  },
  {
    icon: ICONS.security,
    title: "Security",
    desc: "Report a vulnerability.",
    linkLabel: "security@wonderfulagent.ai",
    href: "mailto:security@wonderfulagent.ai",
  },
  {
    icon: ICONS.careers,
    title: "Careers",
    desc: "Come build with us.",
    linkLabel: "View open roles",
    to: "/careers",
  },
];

const INTERESTS = ["Product demo", "Pricing", "Support", "Partnership", "Other"] as const;
type Interest = (typeof INTERESTS)[number];

const EXPECT: { title: string; body: string }[] = [
  { title: "One business day", body: "A real person reads every message and replies within one business day." },
  { title: "No spam, ever", body: "Your details stay client-side here and are only used to answer your note." },
  { title: "Straight to the point", body: "Demos are tailored to your actual roles — not a generic walkthrough." },
];

export function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [interest, setInterest] = useState<Interest>("Product demo");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  // SECURITY: never posts anywhere. All data stays client-side; we just show
  // an inline success state.
  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSent(true);
  }

  return (
    <MarketingPage
      seo={{
        title: "Contact — WonderfulInterview",
        description:
          "Get in touch with WonderfulInterview. Book a demo on your roles, reach support, report a security issue, or explore careers — a real person replies within one business day.",
        path: "/contact",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]),
      }}
    >
      <PageHeader
        eyebrow="CONTACT"
        title="Let's talk about"
        accent="your hiring"
        tail="."
        sub="Whether you want a demo on your own roles, need a hand as a customer, or have something to share — we'd love to hear from you."
      />

      {/* Channel cards */}
      <section className="pb-8">
        <Container>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CHANNELS.map((c) => (
              <div key={c.title} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <Icon>{c.icon}</Icon>
                <h3 className="mt-5 text-lg font-semibold text-warm-ink">{c.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">{c.desc}</p>
                {c.href ? (
                  <a
                    href={c.href}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-warm-accent hover:text-warm-accentDark"
                  >
                    {c.linkLabel}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <div className="mt-4">
                    <MButton to={c.to} variant="outline" className="px-4 py-2 text-sm">
                      {c.linkLabel}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </MButton>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Form + aside */}
      <section className="py-24">
        <Container>
          <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr]">
            {/* Form */}
            <div className="rounded-3xl border border-warm-border bg-warm-card p-7 sm:p-9">
              <Eyebrow>Send a message</Eyebrow>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-warm-ink">Tell us what you need.</h2>

              {sent ? (
                <div className="mt-8 rounded-2xl border border-warm-accent/30 bg-warm-sand/40 p-7">
                  <div className="text-lg font-semibold text-warm-ink">Thanks — we'll reply within one business day.</div>
                  <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">
                    Your message is on its way to our team. In the meantime, feel free to write to{" "}
                    <a href="mailto:hello@wonderfulagent.ai" className="font-semibold text-warm-accent">
                      hello@wonderfulagent.ai
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-warm-ink">
                        Name
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Jane Rivera"
                        autoComplete="name"
                        className="mt-2 w-full rounded-xl border border-warm-border bg-warm-card px-4 py-3 text-sm text-warm-ink outline-none placeholder:text-warm-muted/60 focus:border-warm-accent"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-warm-ink">
                        Work email
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="jane@company.com"
                        autoComplete="email"
                        className="mt-2 w-full rounded-xl border border-warm-border bg-warm-card px-4 py-3 text-sm text-warm-ink outline-none placeholder:text-warm-muted/60 focus:border-warm-accent"
                      />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-company" className="block text-sm font-medium text-warm-ink">
                        Company <span className="text-warm-muted">(optional)</span>
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Acme Inc."
                        autoComplete="organization"
                        className="mt-2 w-full rounded-xl border border-warm-border bg-warm-card px-4 py-3 text-sm text-warm-ink outline-none placeholder:text-warm-muted/60 focus:border-warm-accent"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-interest" className="block text-sm font-medium text-warm-ink">
                        I'm interested in
                      </label>
                      <select
                        id="contact-interest"
                        value={interest}
                        onChange={(e) => setInterest(e.target.value as Interest)}
                        className="mt-2 w-full rounded-xl border border-warm-border bg-warm-card px-4 py-3 text-sm text-warm-ink outline-none focus:border-warm-accent"
                      >
                        {INTERESTS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-warm-ink">
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      required
                      rows={5}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us about the roles you're hiring for, or how we can help."
                      className="mt-2 w-full rounded-xl border border-warm-border bg-warm-card px-4 py-3 text-sm text-warm-ink outline-none placeholder:text-warm-muted/60 focus:border-warm-accent"
                    />
                  </div>

                  <div className="flex flex-col gap-4 pt-1 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-warm-accent px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-warm-accent/20 transition-colors hover:bg-warm-accentDark"
                    >
                      Send message
                      <ArrowRight />
                    </button>
                    <p className="text-sm text-warm-muted">
                      Prefer email? Write to{" "}
                      <a href="mailto:hello@wonderfulagent.ai" className="font-semibold text-warm-accent">
                        hello@wonderfulagent.ai
                      </a>
                    </p>
                  </div>
                </form>
              )}
            </div>

            {/* Aside */}
            <aside>
              <div className="rounded-3xl border border-warm-border bg-warm-sand/40 p-7">
                <h3 className="text-lg font-semibold text-warm-ink">What to expect</h3>
                <ul className="mt-5 space-y-5">
                  {EXPECT.map((item) => (
                    <li key={item.title}>
                      <div className="flex items-start gap-3">
                        <span className="mt-1 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-warm-accent/15 text-warm-accent">
                          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                            <path d="M5 12l5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        <div>
                          <div className="text-[15px] font-semibold text-warm-ink">{item.title}</div>
                          <p className="mt-1 text-sm leading-relaxed text-warm-muted">{item.body}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="pb-24">
        <Container>
          <div className="rounded-4xl border border-warm-border bg-warm-ink px-8 py-14 text-center text-warm-bg sm:px-12">
            <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl">
              Rather just <span className="font-display italic font-normal text-warm-glow">try it</span>?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-warm-bg/70">
              Spin up your first AI interview in minutes — no call required.
            </p>
            <div className="mt-8 flex justify-center">
              <MButton to="/signup" className="px-6 py-3 text-base">
                Start free
                <ArrowRight />
              </MButton>
            </div>
          </div>
        </Container>
      </section>
    </MarketingPage>
  );
}
